// src/modules/production/components/ManufacturingStats.tsx
import React from 'react';
import { Row, Col, Card, Statistic, Spin, Empty, Table } from 'antd';
import {
  AppstoreOutlined,
  FileTextOutlined,
  TrophyOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { ManufacturingStats as StatsType } from '@/services/api/manufacturing';
import type { ColumnsType } from 'antd/es/table';

interface ManufacturingStatsProps {
  stats: StatsType | null;
  loading: boolean;
}

export const ManufacturingStats: React.FC<ManufacturingStatsProps> = ({
  stats,
  loading,
}) => {
  if (loading) {
    return (
      <div className="text-center py-8">
        <Spin size="large" tip="Загрузка статистики..." />
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <Empty description="Нет данных о статистике" />
      </Card>
    );
  }

  // Колонки для таблицы всех произведенных блюд
  const itemsColumns: ColumnsType<any> = [
    {
      title: '№',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Название блюда',
      dataIndex: 'menu_item__name',
      key: 'menu_item__name',
    },
    {
      title: 'Произведено',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      width: 150,
      align: 'right',
      render: (value: number) => (
        <span className="text-lg font-bold text-green-600">{value} шт.</span>
      ),
      sorter: (a, b) => a.total_quantity - b.total_quantity,
    },
    {
      title: 'Операций',
      dataIndex: 'records_count',
      key: 'records_count',
      width: 120,
      align: 'center',
      render: (value: number) => <span className="text-gray-600">{value}</span>,
    },
  ];

  // Колонки для таблицы производства по дням
  const dailyColumns: ColumnsType<any> = [
    {
      title: 'Дата',
      dataIndex: 'day',
      key: 'day',
      render: (date: string) => (
        <span className="font-medium">
          {new Date(date).toLocaleDateString('ru-RU', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>
      ),
    },
    {
      title: 'Произведено',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      width: 150,
      align: 'right',
      render: (value: number) => (
        <span className="text-lg font-bold text-blue-600">{value} шт.</span>
      ),
    },
    {
      title: 'Операций',
      dataIndex: 'records_count',
      key: 'records_count',
      width: 120,
      align: 'center',
      render: (value: number) => <span className="text-gray-600">{value}</span>,
    },
  ];

  return (
    <div>
      {/* Основные метрики */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Всего произведено"
              value={stats.total_stats.total_quantity || 0}
              suffix="шт."
              prefix={<AppstoreOutlined />}
              valueStyle={{ color: '#3f8600', fontSize: '24px' }}
            />
            <div className="text-sm text-gray-500 mt-2">
              За период: {stats.period}
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Записей о производстве"
              value={stats.total_stats.total_records || 0}
              suffix="шт."
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '24px' }}
            />
            <div className="text-sm text-gray-500 mt-2">
              Операций производства
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Период анализа"
              value={stats.period}
              prefix={<CalendarOutlined />}
              valueStyle={{ fontSize: '24px' }}
            />
            <div className="text-sm text-gray-500 mt-2">
              {new Date(stats.date_from).toLocaleDateString('ru-RU')} - {new Date(stats.date_to).toLocaleDateString('ru-RU')}
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Среднее за день"
              value={Math.round((stats.total_stats.total_quantity || 0) / parseInt(stats.period)) || 0}
              suffix="шт."
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#cf1322', fontSize: '24px' }}
            />
            <div className="text-sm text-gray-500 mt-2">
              В среднем производится
            </div>
          </Card>
        </Col>
      </Row>

      {/* Таблица всех произведенных блюд */}
      {stats.top_items && stats.top_items.length > 0 && (
        <Card 
          title="📊 Все произведенные блюда" 
          className="mt-4"
          extra={<span className="text-sm text-gray-500">За выбранный период</span>}
        >
          <Table
            columns={itemsColumns}
            dataSource={stats.top_items}
            rowKey="menu_item__id"
            pagination={false}
            size="middle"
          />
        </Card>
      )}

      {/* Таблица производства по дням */}
      {stats.daily_stats && stats.daily_stats.length > 0 && (
        <Card 
          title="📅 Производство по дням" 
          className="mt-4"
          extra={<span className="text-sm text-gray-500">История производства</span>}
        >
          <Table
            columns={dailyColumns}
            dataSource={stats.daily_stats}
            rowKey="day"
            pagination={false}
            size="middle"
          />
        </Card>
      )}
    </div>
  );
};